import { Button, Spacer, VDBText } from "@components/atoms";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { useTypographyStore } from "@redux/slices/typographySlice";
import React from "react";
import { Dimensions, Linking, View } from "react-native";
import styled, { useTheme } from "styled-components/native";
import { ButtonType } from "@components/atoms/Button/types";
import { SpaceType } from "@components/atoms/Spacer/types";
import { EmptyViewProps } from "./types";
import { buttonCategory } from "./Utils";
import { useTranslation } from "react-i18next";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import { setProductType } from "@redux/slices/appStateSlice";
import { useDispatch } from "react-redux";
import { ProductCategory } from "@utils/constants";
import { VDBIcons } from "@utils/VDBIcons";
import useProductArray from "@components/common/hooks/useProductArray";
import { setFiltersPayload, setPayloadData } from "@redux/slices/searchFilters";
import { useProductPrefix } from "../../hooks/useProductPrefix";
import { css } from "styled-components/native";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import RenderHTML from "react-native-render-html";
import { getProductBorderColor } from "@components/molecules/MatchStoneGalleryModel/Utils";

const EmptyView = ({
  productType,
  sectionName = null,
  isFilterVisible,
  toggleFilterScreen,
}: EmptyViewProps) => {
  const dispatch = useDispatch();
  const { skipFilterScreen } = useProductArray();
  const { t } = useTranslation();
  const theme = useTheme();
  const defaultColor = getProductBorderColor(productType);
  const typography = useTypographyStore();
  const { deviceWidth } = useIsFoldable();
  const screenWidth = deviceWidth - 16;
  const screenHeight = Dimensions.get("window").height;
  const isTablet = theme.isTablet || theme.isLargeScreen;
  const skipFilterScreenForWeb = theme.isLargeScreen
    ? skipFilterScreen(productType)
    : false;
  const productPrefix = useProductPrefix();
  const onButtonPress = () => {
    dispatch(setProductType(productType));

    const isJewelry =
      [ProductCategory.JEWELRY, ProductCategory.LABGROWN_JEWELRY].includes(
        productType
      ) && sectionName;

    if (theme.isLargeScreen && skipFilterScreenForWeb) {
      if (
        [ProductCategory.JEWELRY, ProductCategory.LABGROWN_JEWELRY].includes(
          productType
        )
      ) {
        toggleFilterScreen();
      } else {
        const payload = {};
        dispatch(setFiltersPayload({ sectionName, data: payload }));
        dispatch(setPayloadData({ data: payload, productType }));
        navigate(Routes.FilterSplitScreen, {
          productType,
          fromResultScreen: true,
          sectionName,
          filterSplitStep: 2,
          productPrefix,
        });
      }
    } else {
      navigate(
        Routes.FilterScreen,
        {
          productType,
          fromResultScreen: !!isJewelry,
          sectionName,
          productPrefix,
        },
        { merge: true, pop: true }
      );
    }
  };

  const renderersProps = {
    a: {
      onPress(...args: any[]) {
        const href = args[1];
        if (href) {
          Linking.canOpenURL(href).then((supported) => {
            supported && Linking.openURL(href);
          });
        }
      },
    },
  };

  const renderModifySearchParamLink = () => {
    return (
      <RenderHTML
        source={{
          html: `${translateWithFallback(
            productType,
            "searchResult.emptyView.pleaseModifySearchParams.text"
          )}`,
        }}
        defaultTextProps={{
          selectable: true,
        }}
        tagsStyles={{
          span: {
            ...typography["H3-R"],
            color: theme["core-06"],
          },
          a: { ...typography["H3-M"], color: theme[defaultColor] },
        }}
        renderersProps={renderersProps}
      />
    );
  };

  const translateWithFallback = (product_type: string, key: string) => {
    const modifiedKey = key.replace(
      /^searchResult\./,
      `searchResult.${product_type}.`
    );

    const translated = t(modifiedKey);
    return translated !== modifiedKey ? translated : t(key);
  };

  return (
    <Overlay screenHeight={screenHeight}>
      <View>
        <VDBIcon
          color={theme["core-04"]}
          name={VDBIcons.MAIN_SEARCH}
          size={64}
          variant={IconsVariant.VDBCustom}
        />
      </View>
      <TextContainer>
        <VDBText color={theme["core-05"]} type={typography["H3-M"]}>
          {translateWithFallback(
            productType,
            "searchResult.emptyView.noResults.text"
          )}
        </VDBText>
      </TextContainer>
      <SubTextContainer>
        <VDBText color={theme["core-06"]} type={typography["Body-R"]}>
          {renderModifySearchParamLink()}
        </VDBText>
      </SubTextContainer>

      {!isFilterVisible && (
        <>
          <Spacer size={SpaceType.ExtraLarge} />
          <Button
            category={buttonCategory(productType)}
            type={ButtonType.primary}
            title={t("searchResult.emptyView.changeFilters.button.text")}
            onPress={onButtonPress}
            width={screenWidth}
            maxWidth={isTablet ? 343 : undefined}
            textTypographyStyle="H3-M"
          />
        </>
      )}
    </Overlay>
  );
};

const Overlay = styled.View<{
  screenHeight?: string;
}>`
  ${({ theme }) =>
    !theme.isLargeScreen &&
    css`
      flex: 1;
    `}
  ${({ theme, screenHeight }) =>
    theme.isLargeScreen &&
    css`
      height: ${screenHeight - 200}px;
    `}
  align-items: center;
  justify-content: center;
  padding-bottom: 110px;
`;

const TextContainer = styled.View`
  margin-top: 16px;
`;

const SubTextContainer = styled.View`
  margin-top: 4px;
`;

export default EmptyView;
